import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    TextField,
    Button,
    MenuItem,
    AppBar,
    Toolbar,
    Grid,
    Backdrop,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel
} from "@mui/material";
import api from "../services/api";
import { BackdropStyles, CircularProgressStyles } from "./genral"

/**
 * ShortlistDetailsForm is a React functional component that renders a form for
 * users to provide details about their shortlisted candidates.
 *
 * @return {JSX.Element} A React component that renders the form for shortlisted
 * candidates.
 */
export default function ShortlistDetailsForm() {
    const { token } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        notice_period: "",
        current_salary: "",
        current_salary_currency: "",
        expected_salary: "",
        expected_salary_currency: "",
        visa_status: "",
        remarks: "",
        consent: false
    });
    const [implementerInfo, setImplementerInfo] = useState(null);
    const [dialogError, setDialogError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [redirect, setRedirect] = useState(false);
    const [resumeId, setResumeId] = useState(null);
    const [jdId, setJdId] = useState(null);
    const [dialog, setDialog] = useState({
        open: false,
        title: "",
        message: "",
        type: "info"
    });

    const visaOptionsByCountry = {
        India: [
            "Indian Citizen",
            "OCI Card Holder",
            "Need Sponsorship"
        ],

        "United States": [
            "US Citizen",
            "Green Card",
            "H1B",
            "L1",
            "OPT",
            "Need Sponsorship"
        ],

        "United Kingdom": [
            "UK Citizen",
            "ILR",
            "Skilled Worker Visa",
            "Graduate Visa",
            "Need Sponsorship"
        ],

        Singapore: [
            "Singapore Citizen",
            "Permanent Resident (PR)",
            "Employment Pass (EP)",
            "S Pass",
            "Dependent Pass",
            "Need Sponsorship"
        ],

        Canada: [
            "Canadian Citizen",
            "PR",
            "Work Permit",
            "Study Permit",
            "Need Sponsorship"
        ],

        Australia: [
            "Australian Citizen",
            "PR",
            "482 Visa",
            "Working Holiday Visa",
            "Need Sponsorship"
        ]
    };

    const defaultVisaOptions = [
        "Citizen",
        "Permanent Resident",
        "Work Permit",
        "Dependent Visa",
        "Need Sponsorship"
    ];


    /**
     * Returns the visa options for a given country.
     *
     * @param {string} country - The country for which to get visa options.
     * @return {Array<string>} An array of visa options for the given country,
     * or the default visa options if the country is not specified or not found.
     */
    const getVisaOptions = (country) => {
        return visaOptionsByCountry[country] || defaultVisaOptions;
    };

    /**
     * Updates the form data with the new value of the input field.
     *
     * @param {Object} e - The event object containing information about the input field.
     * @param {string} e.target.name - The name of the input field.
     * @param {string} e.target.value - The new value of the input field.
     * @return {void} This function does not return anything.
     */
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const currencyOptions = (implementerInfo?.currencies || implementerInfo?.currency || "")
        .split(",")
        .map(c => c.trim())
        .filter(Boolean);

    /**
     * Resolves the token and sets the resume and job details.
     *
     * @return {Promise<void>} A Promise that resolves when the job details are fetched.
     */
    useEffect(() => {
        if (!token) return;

        /**
         * Resolves the token and sets the resume and job details.
         *
         * @return {Promise<void>} A Promise that resolves when the job details are fetched.
         * @throws {Error} If the job details can't be fetched.
         */
        const resolveToken = async () => {
            try {
                const res = await api.get(`/resolve-token/${token}/`);
                setResumeId(res.data.resume_id);
                setJdId(res.data.jd_id);
            } catch {
                setDialog({
                    open: true,
                    title: "Invalid Link",
                    message: "This link is invalid or has expired.",
                    type: "error"
                });
            }
        };

        resolveToken();
    }, [token]);

    /**
     * Fetches the shortlist details from the server based on the resume ID and job detail ID.
     *
     * This function fetches the shortlist details from the server based on the resume ID and job detail ID.
     * If the details have already been submitted, it redirects the user to the confirmation page.
     * Otherwise, it sets the implementer info state with the fetched data.
     *
     * @return {Promise<void>} A Promise that resolves when the shortlist details are fetched and set.
     * @throws {Error} If the shortlist details can't be fetched or the response is invalid.
     */
    useEffect(() => {
        if (!resumeId || !jdId) return;

        const value = `${resumeId}_${jdId}`;

        api.get(`/shortlist-details/${value}/`)
            .then(res => {
                if (res.data.submitted) {
                    navigate(`/shortlist-confirm/${value}`);
                    return;
                }
                setImplementerInfo(res.data);
            })
            .catch(() => navigate("Invalid Link"));

    }, [resumeId, jdId]);


    /**
     * Submits the shortlist details form data to the server.
     *
     * This function validates the form data and submits it to the server.
     * If the consent is not provided, it sets the appropriate error message.
     * Otherwise, it sets the submitted state to true and makes a POST request
     * to the server with the form data and token. If the response is successful,
     * it sets the dialog error message to indicate successful submission and
     * redirects the user after a delay to the confirmation page. If the response
     * is not successful, it sets the dialog error message to indicate failure.
     *
     * @return {void}
     */
    const handleSubmit = () => {

        if (!formData.consent) {
            setDialogError(
                <span style={{ color: "#ff4d4d" }}>
                    Please provide consent to proceed.
                </span>
            );
            return;
        }

        setSubmitted(true);

        api.post("/submit-shortlist-details/", {
            token,
            ...formData,
            submitted_by: "candidate"
        })
            .then((response) => {
                if (response.data) {
                    setDialogError(
                        <span style={{ color: "green" }}>
                            Data submitted successfully
                        </span>
                    );

                    setTimeout(() => {
                        setRedirect(true);
                        navigate(`/shortlist-confirm/${token}`);
                    }, 2000);

                } else {
                    setDialogError(
                        <span style={{ color: "#ff4d4d" }}>Data submission failed</span>
                    );
                    setSubmitted(false);
                }
            })
            .catch(() => {
                setDialogError(
                    <span style={{ color: "#ff4d4d" }}>Something went wrong</span>
                );
                setSubmitted(false);
            });
    };

    return (
        <>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backdropFilter: "blur(10px)",
                    background: "rgba(255,255,255,0.9)",
                    borderBottom: "1px solid #eee",
                }}
            >
                <Toolbar>
                    <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{
                            background: "linear-gradient(90deg,#6366f1,#ec4899)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }}
                    >
                        ITConnectUS
                    </Typography>
                </Toolbar>
            </AppBar>

            <Box display="flex" justifyContent="center" mt={6}>
                <Card sx={{ p: 4, maxWidth: 600, width: "100%" }}>
                    <Typography variant="h5" gutterBottom>
                        Hi {implementerInfo?.candidate_name || "Candidate"},
                        thank you for your interest in the position of{" "}
                        <strong>{implementerInfo?.job_title}</strong>.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        Additional Details Required
                    </Typography>

                    <TextField
                        label="Notice Period (Days)"
                        name="notice_period"
                        fullWidth
                        margin="normal"
                        value={formData.notice_period}
                        onChange={handleChange}
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                label="Current Salary"
                                name="current_salary"
                                type="number"
                                fullWidth
                                value={formData.current_salary}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                select
                                label="Currency"
                                name="current_salary_currency"
                                fullWidth
                                value={formData.current_salary_currency}
                                onChange={handleChange}
                            >
                                {currencyOptions.map((cur) => (
                                    <MenuItem key={cur} value={cur}>
                                        {cur}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                    <br />
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                label="Expected Salary"
                                name="expected_salary"
                                type="number"
                                fullWidth
                                inputProps={{ maxLength: 14 }}
                                value={formData.expected_salary}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                select
                                label="Currency"
                                name="expected_salary_currency"
                                fullWidth
                                value={formData.expected_salary_currency}
                                onChange={handleChange}
                            >
                                {currencyOptions.map((cur) => (
                                    <MenuItem key={cur} value={cur}>
                                        {cur}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>

                    <TextField
                        select
                        name="visa_status"
                        fullWidth
                        margin="normal"
                        value={formData.visa_status}
                        onChange={handleChange}
                        label="Visa Status *"
                    >
                        {getVisaOptions(implementerInfo?.country).map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Remarks"
                        name="remarks"
                        multiline
                        fullWidth
                        rows={4}
                        margin="normal"
                        value={formData.remarks}
                        onChange={handleChange}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.consent}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        consent: e.target.checked
                                    })
                                }
                            />
                        }
                        label="I agree to share my profile with the client for this job opportunity."
                    />

                    <Grid item xs={12}>
                        <div style={{ width: "100%" }}>
                            {dialogError}
                        </div>
                    </Grid>

                    <Grid item>
                        {submitted ? (
                            redirect ? null : (
                                <>
                                    <Button variant="contained"
                                        sx={{ textTransform: "none", mt: 3 }} size="small" disabled>
                                        Submit & Continue
                                    </Button>
                                    <Backdrop open={true} sx={BackdropStyles()}>
                                        <CircularProgress sx={{ ...CircularProgressStyles() }} />
                                    </Backdrop>
                                </>
                            )
                        ) : (
                            <Button variant="contained"
                                sx={{ textTransform: "none", mt: 3 }} size="small" onClick={handleSubmit}>
                                Submit & Continue
                            </Button>
                        )}
                    </Grid>
                </Card>
                <Dialog
                    open={dialog.open}
                    onClose={() => setDialog({ ...dialog, open: false })}
                >
                    <DialogTitle sx={{ fontWeight: 700 }}>
                        {dialog.title}
                    </DialogTitle>

                    <DialogContent>
                        <Typography fontSize="14px">
                            {dialog.message}
                        </Typography>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            variant="contained"
                            onClick={() => setDialog({ ...dialog, open: false })}
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}